/** Notification preferences + web push (v1 notifications.py). Per-event in-app /
 * push toggles (GET/POST /api/notifications/prefs), the VAPID key (/vapid), and
 * device push subscription (/subscribe, /unsubscribe) — all on the ALREADY-
 * DEPLOYED backend. In-app + prefs are safe; enabling push asks the browser for
 * permission and registers a minimal push-only service worker. No backend
 * changes. */
import { backendFetch } from "../../lib/backend";

export interface EvPref { inapp: boolean; push: boolean }
export type NotifPrefs = Record<string, unknown>; // {<ev>:{inapp,push}, _num:"own"|"all"}

/** The notifiable events (backend notif_prefs keys), with labels for the UI.
 * dailyDigest + showing joined 2026-07-25 — they used to arrive as SMS texts. */
export const NOTIF_EVENTS: { ev: string; label: string }[] = [
  { ev: "dailyDigest", label: "Morning digest" },
  { ev: "newLead", label: "New lead" },
  { ev: "comingSoon", label: "Mark coming-soon lead" },
  { ev: "zillowStatus", label: "Zillow lead pending / sold (→ Dead Deal)" },
  { ev: "inboundText", label: "Inbound text" },
  { ev: "taskAssigned", label: "Task assigned to me" },
  { ev: "taskDue", label: "Task due" },
  { ev: "calendarReminder", label: "Calendar event reminder" },
  { ev: "closingReminder", label: "Closing reminder" },
  { ev: "showing", label: "Showing update" },
  { ev: "newOffer", label: "New offer" },
  { ev: "stageChange", label: "Deal stage change" },
  { ev: "priceDrop", label: "Price reduction" },
  { ev: "backOnMarket", label: "Back on market" },
  { ev: "mention", label: "@ Mention" },
  { ev: "signedDoc", label: "Document signed" },
  { ev: "materialPrice", label: "Material price change" },
  { ev: "scraperIssue", label: "Scraper issue" },
];

/** Prefs with the push channel ON for every event — applied when a device
 * enables push, so enrolling actually delivers everything (untick to opt out). */
export function withAllPush(prefs: NotifPrefs): NotifPrefs {
  return NOTIF_EVENTS.reduce((p, { ev }) => withPref(p, ev, "push", true), prefs);
}

/** Events whose in-app channel is ON by default (backend _DEFAULT_INAPP_ON). */
const DEFAULT_INAPP_ON = new Set(NOTIF_EVENTS.map((e) => e.ev));

/** Effective toggle for one event/channel, honoring the stored prefs + defaults
 * (push defaults off until enabled on a device — mirrors the backend). */
/** Kinds added after a user saved prefs inherit a sibling's channels until
 * set explicitly (mirrors the backend's _PREF_INHERIT). */
const PREF_INHERIT: Record<string, string> = { calendarReminder: "taskDue", comingSoon: "newLead", zillowStatus: "newLead" };

export function prefOn(prefs: NotifPrefs, ev: string, ch: "inapp" | "push"): boolean {
  const node = prefs[ev];
  if (node && typeof node === "object" && !Array.isArray(node) && ch in (node as Record<string, unknown>)) {
    return (node as Record<string, unknown>)[ch] === true;
  }
  const parent = PREF_INHERIT[ev];
  if (parent) {
    const pn = prefs[parent];
    if (pn && typeof pn === "object" && !Array.isArray(pn) && ch in (pn as Record<string, unknown>)) {
      return (pn as Record<string, unknown>)[ch] === true;
    }
  }
  return ch === "inapp" ? DEFAULT_INAPP_ON.has(ev) : false;
}

/** Return prefs with one event/channel set (immutably). */
export function withPref(prefs: NotifPrefs, ev: string, ch: "inapp" | "push", on: boolean): NotifPrefs {
  const node = (prefs[ev] && typeof prefs[ev] === "object" && !Array.isArray(prefs[ev]) ? { ...(prefs[ev] as Record<string, unknown>) } : {}) as Record<string, unknown>;
  // seed the other channel from its effective value so we don't lose it
  const other = ch === "inapp" ? "push" : "inapp";
  if (!(other in node)) node[other] = prefOn(prefs, ev, other);
  node[ch] = on;
  return { ...prefs, [ev]: node };
}

/** The "new text / call" number scope (v1 _num): notify for every org number or just mine. */
export function numScope(prefs: NotifPrefs): "all" | "own" {
  return prefs["_num"] === "own" ? "own" : "all";
}
export function withNumScope(prefs: NotifPrefs, v: "all" | "own"): NotifPrefs {
  return { ...prefs, _num: v };
}

// ---- network ----------------------------------------------------------------

export async function fetchPrefs(token: string | null, signal?: AbortSignal): Promise<NotifPrefs> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/notifications/prefs", { timeoutMs: 25000, ...(signal ? { signal } : {}) });
  const p = j["prefs"];
  return p && typeof p === "object" && !Array.isArray(p) ? (p as NotifPrefs) : {};
}

export async function savePrefs(token: string | null, prefs: NotifPrefs): Promise<void> {
  await backendFetch(token, "/api/notifications/prefs", { method: "POST", body: { prefs }, timeoutMs: 25000 });
}

export async function fetchVapid(token: string | null, signal?: AbortSignal): Promise<{ publicKey: string; enabled: boolean }> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/notifications/vapid", { timeoutMs: 20000, ...(signal ? { signal } : {}) });
  return { publicKey: typeof j["publicKey"] === "string" ? (j["publicKey"] as string) : "", enabled: j["enabled"] === true };
}

// ---- web push (device) ------------------------------------------------------

export function pushSupported(): boolean {
  try {
    return typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof window !== "undefined" && "PushManager" in window && "Notification" in window;
  } catch {
    return false;
  }
}

/** iPhone/iPad Safari only exposes web push AFTER the site is added to the
 * Home Screen (iOS 16.4+). True = show the "Add to Home Screen" hint. */
export function iosNeedsHomeScreen(): boolean {
  try {
    if (typeof navigator === "undefined" || typeof window === "undefined") return false;
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches === true ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    return ios && !standalone && !pushSupported();
  } catch {
    return false;
  }
}

/** Settings "Send test push" — backend pushes one notification to THIS account. */
export async function sendTestPush(token: string | null): Promise<{ pushEnabled: boolean }> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/notifications/test", { method: "POST", body: {}, timeoutMs: 25000 });
  return { pushEnabled: j["pushEnabled"] === true };
}

/** VAPID key: base64url string → Uint8Array for applicationServerKey. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Register the push service worker, ask permission, subscribe, and file the
 * subscription with the backend. Returns the browser endpoint on success. */
export async function enablePush(token: string | null, vapidKey: string): Promise<string> {
  if (!pushSupported()) throw new Error("Push isn't supported on this browser.");
  if (!vapidKey) throw new Error("Push isn't configured on the server yet.");
  const reg = await navigator.serviceWorker.register("/push-sw.js");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications permission was declined.");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
  });
  await backendFetch(token, "/api/notifications/subscribe", { method: "POST", body: { sub: sub.toJSON() }, timeoutMs: 25000 });
  return sub.endpoint;
}

/** Unsubscribe this device (best-effort browser + backend). */
export async function disablePush(token: string | null): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    try { await backendFetch(token, "/api/notifications/unsubscribe", { method: "POST", body: { endpoint }, timeoutMs: 20000 }); } catch { /* ignore */ }
  }
}
